export const SITE_URL = 'https://fontifytext.com';
export const SITE_NAME = 'FontifyText';
export const CONTACT_EMAIL = 'fontifygen@gmail.com';
export const SITE_OG_IMAGE = 'https://fontifytext.com/og-image.webp';

export const HOME_TITLE = 'Free Font Generator - Copy & Paste Fancy Text | FontifyText';

// Schema description. Also feeds organizationSchema, which every page renders,
// so it is deliberately held constant while the meta description below is
// under test — otherwise the change would not be isolated to the homepage.
export const HOME_DESCRIPTION =
  'Create copy-paste Unicode text styles for bios, captions, messages, and usernames. Preview fancy, cursive, bold, gothic, and bubble text instantly.';

// ── CTR TEST: homepage meta description ──────────────────────────────────────
// Variant B, live 2026-08-04. Only the meta description changes; URL, H1, title
// and every other page stay as they were. See
// seo/evidence/homepage-ctr-test/BASELINE.md for the control text and how to
// read the result. Revert this constant to HOME_DESCRIPTION to end the test.
export const HOME_META_DESCRIPTION =
  'Type once and preview 474 copy-paste font styles: fancy, cursive, bold and gothic text for an Instagram bio, TikTok caption or Discord name. Free, no signup.';

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  alternateName: 'FontifyText Font Generator',
  url: `${SITE_URL}/`,
};

// Brought up to the same completeness as the tool pages, which carry image,
// alternateName, browserRequirements and featureList. Deliberately no HowTo or
// BreadcrumbList here: Google retired HowTo rich results in 2023, and a
// breadcrumb on the root page describes a path with nothing in it.
export const homepageWebApplicationSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'FontifyText',
  alternateName: 'Font Generator',
  url: `${SITE_URL}/`,
  image: SITE_OG_IMAGE,
  applicationCategory: 'UtilityApplication',
  operatingSystem: 'All',
  browserRequirements: 'Requires JavaScript. Requires HTML5.',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
  description: HOME_DESCRIPTION,
  featureList: [
    '474 copy-paste Unicode text styles',
    '32 style categories',
    'Live preview as you type',
    'One-click copy',
    'Works in Instagram, TikTok, Discord and WhatsApp',
    'No sign-up required',
    'Free to use',
  ],
};

export const faqItems = [
  {
    question: 'How do I copy and paste fancy fonts?',
    answer:
      'Type your text into the FontifyText input box. All font styles update instantly below as you type. Click the Copy button next to the style you want, then paste it directly into any Instagram bio, TikTok caption, Discord username, WhatsApp message, or any other text field. No app or sign-up is needed.',
  },
  {
    question: 'Why do these fonts work on Instagram and TikTok?',
    answer:
      'These are Unicode characters, not actual font files. Instagram, TikTok, Discord, and most apps accept standard Unicode text in their input fields. Because each styled letter is its own unique Unicode character, the style travels with the text wherever you paste it — just like any other letter would.',
  },
  {
    question: 'Do these fonts work on iPhone and Android?',
    answer:
      'Yes. All font styles on FontifyText use Unicode characters that are part of every modern operating system. They display correctly on iPhone (iOS 12+), Android (7.0+), and desktop browsers without installing anything. If a very old device shows a blank box, switch to bold or cursive styles — these have the widest device support.',
  },
  {
    question: 'Which font style works best for an Instagram bio?',
    answer:
      'Cursive script (𝓛𝓲𝓴𝓮 𝓣𝓱𝓲𝓼) works best for lifestyle and personal brands. Bold (𝗟𝗶𝗸𝗲 𝗧𝗵𝗶𝘀) is best when one line needs to stand out. Small caps (ʟɪᴋᴇ ᴛʜɪꜱ) is the safest cross-device choice. Use one style for your name or tagline and plain text for the rest — mixing too many styles in one bio looks cluttered.',
  },
  {
    question: 'Can I use fancy fonts for Free Fire or gaming usernames?',
    answer:
      'Yes. Free Fire, PUBG Mobile, Mobile Legends, and many other games accept Unicode characters in player display names. Use the Cool Fonts or Fancy Fonts category on FontifyText, copy the style you want, and paste it into your game name field. Bold, symbolic, and fullwidth styles are the most popular choices for gaming usernames.',
  },
];

export const homepageFaqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'FontifyText',
  alternateName: 'Font Generator',
  url: SITE_URL,
  logo: `${SITE_URL}/fontify-text-logo.png`,
  description: HOME_DESCRIPTION,
  founder: {
    '@type': 'Person',
    name: 'Abdul Wahab',
    jobTitle: 'Typography & Unicode Specialist',
    url: `${SITE_URL}/about/`,
  },
  sameAs: [
    'https://www.facebook.com/profile.php?id=61590911693732',
    'https://www.instagram.com/fontifytext/',
    'https://www.pinterest.com/fontifytext/',
  ],
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Customer Support',
    email: CONTACT_EMAIL,
  },
  potentialAction: {
    '@type': 'UseAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/`,
      actionPlatform: ['DesktopWebPlatform', 'MobileWebPlatform'],
    },
  },
};

export const slugifyCategory = (category) =>
  category
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const formatCategoryName = (category) =>
  category.trim().replace(/\s+/g, ' ');

export const getUniqueCategories = (fontsData) => {
  const seenSlugs = new Set();
  const prioritySlugs = ['fancy-fonts', 'cool-fonts', 'whatsapp-fonts'];
  const sortCategories = (items) =>
    [...items].sort((a, b) => {
      const aPriority = prioritySlugs.indexOf(slugifyCategory(a));
      const bPriority = prioritySlugs.indexOf(slugifyCategory(b));

      if (aPriority !== -1 || bPriority !== -1) {
        if (aPriority === -1) return 1;
        if (bPriority === -1) return -1;
        return aPriority - bPriority;
      }

      return 0;
    });

  const categories = Object.keys(fontsData).filter((category) => {
    const slug = slugifyCategory(category);

    if (!slug || seenSlugs.has(slug)) {
      return false;
    }

    seenSlugs.add(slug);
    return true;
  });

  return sortCategories(categories);
};

export const getCategoryPath = (category) => `/fonts/${slugifyCategory(category)}/`;

export const getCategorySeoName = (categoryName) =>
  categoryName
    .replace(/\s+fonts?$/i, '')
    .replace(/\s+generator$/i, '')
    .trim();

export const CATEGORY_META_DESCRIPTIONS = {
  'bubble-fonts':
    'Bubble font generator - copy and paste circled Unicode letters for Instagram bios, Discord names, and gaming usernames. Outlined and filled styles. Free.',
  'bold-fonts':
    'Bold text generator - copy and paste bold Unicode letters for Instagram bios, email subjects, Discord names, captions, and messages. No sign-up.',
  'cursive-fonts':
    'Preview Bold Script cursive Unicode styles live, then copy and paste flowing script text for Instagram bios, TikTok captions, and usernames. Free.',
  'script-fonts':
    'Create elegant script text in seconds. Type your words, preview stylish cursive and calligraphy letters, then copy and paste them anywhere for free.',
  'monospace-fonts':
    'Convert normal text into monospace Unicode letters. Create typewriter, terminal and code-style text to copy and paste anywhere for free.',
  'gothic-fonts':
    'Gothic text generator - copy and paste Fraktur blackletter Unicode for dark aesthetic bios, gaming usernames, captions, and profiles. Free.',
  'italics-fonts':
    'Italics font generator - copy and paste slanted Unicode text for Instagram bios, Discord names, captions, and posts where CSS italic is stripped.',
  'fancy-fonts':
    'Fancy font generator - copy and paste decorative Unicode styles for Instagram bios, Free Fire names, usernames, and social captions. Free.',
  'glitch-fonts':
    'Turn plain text into corrupted, glitchy Unicode letters - preview live, then copy and paste for Discord bios, horror profiles, and creepypasta captions.',
  'creepy-fonts':
    'Creepy font generator - copy and paste spooky Unicode text for Halloween captions, horror Discord servers, dark bios, and usernames. Free.',
  'aesthetic-fonts':
    'Aesthetic font generator - copy and paste aesthetic text in vaporwave, Y2K, soft girl and cottagecore Unicode styles. Preview live, 20+ styles, free.',
  'cool-fonts':
    'Cool font generator - copy and paste distinctive Unicode text for Free Fire names, Discord nicknames, gaming profiles, and social bios. Free.',
  'small-fonts':
    'Small caps generator - copy and paste small capital letters, superscript, and subscript Unicode text for bios, usernames, captions, formulas, and notes. Free.',
  'symbolic-fonts':
    'Symbolic font generator - copy and paste symbol-alphabet Unicode for gaming usernames, Free Fire names, clan tags, and social profiles. Free.',
  'calligraphy-fonts':
    'Calligraphy font generator copy paste - elegant Unicode script, Fraktur, and italic text for wedding invites, formal bios, and aesthetic posts. Free.',
  'decorated-fonts':
    'Decorated font generator - copy and paste text wrapped in Unicode ornaments for announcements, bios, usernames, and festive captions. Free.',
  'swirly-fonts':
    'Swirly font generator - copy and paste looping ornamental Unicode text for aesthetic Instagram bios, Pinterest titles, and TikTok captions. Free.',
  'zalgo-fonts':
    'Create corrupted Unicode text with this Zalgo text generator. Compare several styles and copy your preferred result for chats, captions, and memes.',
  'cross-out-fonts':
    'Cross out text generator - copy and paste Unicode strikethrough text for Instagram, Discord, WhatsApp, Facebook, and X. Free.',
  'heart-decorated-fonts':
    'Wrap any name or line in hearts and copy it straight out. Heart text styles for bios, usernames, captions and messages - free, no sign-up.',
  'cursed-fonts':
    'Type a word and get it back in twisted Greek, Cyrillic and phonetic letters. Copy cursed text for Discord, captions and usernames - free.',
  'twitter-fonts':
    'Free X/Twitter font generator - type once, preview 20+ Unicode styles live, then copy and paste into your post, bio, or display name instantly.',
  'instagram-fonts':
    'Instagram font generator - copy and paste bold, cursive, and aesthetic Unicode for your bio, captions, and Stories. No app needed.',
  'facebook-fonts':
    'Free Facebook font generator - type once, preview stylish Unicode live, then copy and paste into your bio, post, group name, or Page headline.',
  'discord-fonts':
    'Discord font generator - copy and paste stylish Unicode text for server names, channel names, bios, and nicknames. No bot or signup needed.',
  'twitch-fonts':
    'Twitch font generator - copy and paste styled Unicode text for stream titles, panel descriptions, bios, and channel branding. Free.',
  'whatsapp-fonts':
    'WhatsApp font generator - copy and paste stylish text for messages, status, profile bios, and names. Bold, cursive, bubble, Gothic, and more.',
  'backwards-text':
    'Backwards text generator copy and paste - reverse text, mirror letters, flip words, and create upside-down text online for free.',
  'ethereal-fonts':
    'Ethereal font generator - copy and paste dreamy script, Fraktur, double-struck, and small caps Unicode text for soft aesthetic bios and captions. Free.',
  'serif-fonts':
    'Serif font generator - copy and paste classic serif Unicode text in bold, italic, small caps, blackletter, and double-struck styles for bios and titles. Free.',
  'underline-text':
    'Underline text generator copy and paste - create underlined, double, wavy, dotted, and boxed Unicode text for Instagram bios, Discord, and Google Docs. Free.',
  'strikethrough-text':
    'Copy crossed-out text in 8 Unicode strikethrough styles: classic, bold, italic, script, thin, double, diagonal and boxed. Type it, tap copy, paste anywhere.',
  'cute-fonts':
    'Cute font generator copy and paste - kawaii bubble, bunny, kitty, sparkle, and heart Unicode text styles for Instagram bios, TikTok, and Discord. Free.',
};
export const CATEGORY_HERO_DESCRIPTIONS = {
  'bubble-fonts':
    'Preview outlined and filled bubble letter styles live as you type, then copy and paste them into your bio, username, or chat.',
  'bold-fonts':
    'Generate bold Unicode text that works in Instagram bios, email subjects, and Discord where CSS bold is stripped. Preview and copy instantly.',
  'cursive-fonts':
    'Preview Bold Script and regular Script cursive styles live, then copy your favourite flowing look for Instagram, TikTok, or romantic captions.',
  'script-fonts':
    'Turn normal text into elegant script letters instantly. Enter your text below, choose a style, and copy and paste it into Instagram, TikTok, Discord, Facebook, messages, or profiles.',
  'monospace-fonts':
    'Convert normal text into clean monospace Unicode letters. Type your name, bio, caption, or message below, then copy your favorite fixed-width style.',
  'gothic-fonts':
    'Preview Fraktur and Bold Fraktur blackletter Gothic styles as you type, then copy the dark medieval look that fits your username or horror-themed bio.',
  'italics-fonts':
    'Preview italic Unicode styles, then copy the one that adds editorial emphasis to your bio, caption, or message.',
  'fancy-fonts':
    'Browse decorative fancy font styles updating live as you type. Compare every option simultaneously before copying the style you want for your profile or game name.',
  'glitch-fonts':
    'Preview distorted glitch text styles, then copy the corrupted look for your Discord bio, username, or creepypasta caption.',
  'creepy-fonts':
    'Preview spooky Unicode text styles with horror ornaments and dark symbols, then copy the creepiest style for your Halloween caption or horror-themed username.',
  'aesthetic-fonts':
    'Preview soft vaporwave, Y2K, and minimal aesthetic Unicode styles as you type, then copy the one that matches your Instagram or TikTok aesthetic direction.',
  'cool-fonts':
    'Preview distinctive cool font styles built for gaming names, Discord nicknames, and social bios. Compare every option live and copy the one that stands out most.',
  'small-fonts':
    'Create copyable small caps, superscript, and subscript Unicode for bios, usernames, captions, formulas, and notes.',
  'symbolic-fonts':
    'Preview symbol-alphabet Unicode styles that replace letters with visually exotic characters, then copy the style for your gaming username or social bio.',
  'calligraphy-fonts':
    'Preview elegant Unicode calligraphy styles as you type, then copy and paste the formal script look for your wedding caption, aesthetic bio, or Canva design.',
  'decorated-fonts':
    'Preview text wrapped in Unicode ornaments and geometric symbols, then copy the style that fits your announcement, username, or bio.',
  'swirly-fonts':
    'Turn plain text into flowing swirly letters for bios, captions, board titles, and aesthetic names. Type once, preview live styles, then copy the version that still reads clearly.',
  'zalgo-fonts':
    'Create creepy, corrupted Unicode text for chats, captions, memes, and horror writing. Compare generated Zalgo styles and copy the result you prefer.',
  'cross-out-fonts':
    'Preview Unicode strikethrough text that works across Instagram, Discord, WhatsApp, and X. Copy the crossed-out style instantly.',
  'heart-decorated-fonts':
    'Preview text decorated with hearts and Unicode outlines, then copy the sweetest style for your Valentine caption or couple bio.',
  'cursed-fonts':
    'Preview dark meme-aesthetic cursed Unicode styles with distorted letterforms, then copy the one that fits your Discord humour server or ironic social media post.',
  'twitter-fonts':
    'Preview bold and styled Unicode text for X posts, bios, and display names. Copy the look that makes your profile or post stand out.',
  'instagram-fonts':
    'Make your Instagram bio or caption stand out without an app. Type once, compare bold, cursive, aesthetic, and fancy Unicode styles, then copy the cleanest result.',
  'facebook-fonts':
    'Preview styled Unicode text for your Facebook bio, posts, group names, and Page headlines. Works in Messenger too. Copy the look that distinguishes your profile.',
  'discord-fonts':
    'Preview Unicode styles for Discord server names, channel names, bios, and nicknames where Markdown formatting does not work. Copy and paste directly.',
  'twitch-fonts':
    'Preview styled Unicode text for Twitch stream titles, channel panels, and bio descriptions. Makes your channel text more branded. Copy the look you want instantly.',
  'whatsapp-fonts':
    'Preview Unicode font styles built for WhatsApp, from clean bold text for business messages to ornamental script for status captions.',
  'backwards-text':
    'Reverse, mirror, flip, and copy backwards text in eight live modes. Built for quick copy-paste text, usernames, captions, puzzles, and messages.',
  'ethereal-fonts':
    'Preview dreamy script, Fraktur, double-struck, and small caps Unicode styles as you type, then copy the soft, otherworldly look for your bio or caption.',
  'serif-fonts':
    'Preview six classic serif Unicode styles — bold, italic, bold italic, small caps, blackletter, and double-struck — as you type, then copy the look that fits your bio, title, or brand.',
  'underline-text':
    'Preview classic, bold, italic, script, double, wavy, dotted, and boxed underline styles as you type, then copy Unicode underlined text that works anywhere plain text does — no CSS or formatting button needed.',
  'strikethrough-text':
    'Preview classic, bold, italic, script, double, thin, diagonal, and boxed strikethrough styles as you type, then copy crossed-out Unicode text that works anywhere plain text does — no CSS or formatting button needed.',
  'cute-fonts':
    'Preview kawaii bubble, kitty, bunny, sparkle, and heart-decorated Unicode text styles as you type, then copy the cutest look for your bio, caption, or username.',
};
export const CATEGORY_TITLES = {
  'instagram-fonts': 'Instagram Font Generator - Bio Fonts | FontifyText',
  'facebook-fonts': 'Facebook Font Generator - Copy & Paste Fonts | FontifyText',
  'twitter-fonts': 'X (Twitter) Font Generator - Copy & Paste | FontifyText',
  'discord-fonts': 'Discord Font Generator – Copy & Paste Name Styles',
  'aesthetic-fonts': 'Aesthetic Font Generator — Copy and Paste Aesthetic Text',
  'bold-fonts': 'Bold Text Generator - Copy & Paste Letters | FontifyText',
  'cursive-fonts': 'Cursive Font Generator - Script Copy & Paste | FontifyText',
  // Day 9, 2026-10-20. Previous title, for rollback:
  //   'Script Font Generator (𝓒𝓸𝓹𝔂 & 𝓟𝓪𝓼𝓽𝓮) – Free'
  // Those nine characters are Mathematical Alphanumeric, not letters: the title
  // read as "Copy & Paste" and contained neither word. Checked 2026-10-20 - the
  // ASCII strings "Copy" and "Paste" were absent from it. Every script, cursive
  // and calligraphy query in the exports here is phrased with copy and paste,
  // so the title was aiming at that demand with characters that cannot match it.
  // This is also the hazard the page's own prose explains.
  'script-fonts': 'Script Font Generator – Copy & Paste Script Text',
  'monospace-fonts': 'Monospace Font Generator (𝙲𝚘𝚙𝚢 & 𝙿𝚊𝚜𝚝𝚎) – Free',
  'gothic-fonts': 'Gothic Text Generator - Copy & Paste Fonts | FontifyText',
  // These seven fell through to the generic `${seoName} Generator - Copy & Paste
  // Fonts` template, producing seven near-identical titles with no head noun.
  // The noun in each is the one that family's own queries use.
  'creepy-fonts': 'Creepy Font Generator - Copy & Paste | FontifyText',
  'decorated-fonts': 'Decorated Font Generator - Copy & Paste | FontifyText',
  'bubble-fonts': 'Bubble Font Generator - Bubble Letters | FontifyText',
  'symbolic-fonts': 'Symbolic Font Generator - Copy & Paste | FontifyText',
  'twitch-fonts': 'Twitch Font Generator - Copy & Paste | FontifyText',
  'italics-fonts': 'Italic Font Generator - Italics Copy & Paste | FontifyText',
  'cross-out-fonts': 'Cross Out Text Generator - Crossed Out Font | FontifyText',
  'fancy-fonts': 'Fancy Font Generator - Copy & Paste Free | FontifyText',
  'whatsapp-fonts': 'WhatsApp Font Generator - Stylish Text | FontifyText',
  'glitch-fonts': 'Glitch Font Generator - Distorted Unicode Text | FontifyText',
  'cool-fonts': 'Cool Font Generator - Copy & Paste Free | FontifyText',
  // Day 8, 2026-10-19. Previous title, for rollback:
  //   'Heart Font Generator - Heart Text Styles | FontifyText'
  // It said "Heart" three times and spent 13 of its 53 characters on a brand
  // suffix that no heart query contains. The Week 4 snippet window on this page
  // closed 2026-09-06 and its result was never read, so Search Console still
  // holds that period; this change is dated so a later export can tell the two
  // apart. Judge on or after 2026-11-02.
  'heart-decorated-fonts': 'Heart Font Generator – Copy & Paste Heart Text',
  'small-fonts': 'Small Caps Generator — Small Capital Letters Copy and Paste',
  // "Cursed Text" moved to cursed-fonts below. Three pages carried the phrase
  // in their title while the tool built for it did not, so this one keeps the
  // term it should actually win.
  'zalgo-fonts': 'Zalgo Text Generator — Glitched Unicode Copy & Paste',
  // Without an entry the fallback produced "Cursed Generator", dropping the
  // head noun entirely — nobody searches that.
  'cursed-fonts': 'Cursed Font Generator - Distorted & Glitched Text',
  'backwards-text': 'Backwards Text Generator Copy and Paste | FontifyText',
  'ethereal-fonts': 'Ethereal Font Generator | FontifyText',
  'calligraphy-fonts': 'Calligraphy Font Generator | FontifyText',
  'serif-fonts': 'Serif Font Generator - Classic Serif Text | FontifyText',
  // Day 21 title test. Was 'Underline Text Generator — Underlined Text Copy and
  // Paste', which opened with the same three words as the blog guide's title.
  // The count is the number of styles the page actually renders: 8.
  'underline-text': 'Underline Text Generator — Underlined Text Copy and Paste',
  // Day 25. Was 'Strikethrough Text Generator — Cross Out Text Online', which
  // is all but identical to the blog guide's title. The count is the number of
  // style cards the page renders: 8.
  'strikethrough-text': 'Strikethrough Text Copy & Paste — 8 Unicode Styles',
  'cute-fonts': 'Cute Font Generator — Cute Text Copy and Paste',
};

export const CATEGORY_H1S = {
  'facebook-fonts': 'Facebook Font Generator - Copy & Paste',
  'twitter-fonts': 'X (Twitter) Font Generator - Copy & Paste',
  'script-fonts': 'Script Font Generator – Copy and Paste Script Text',
  'monospace-fonts': 'Monospace Font Generator – Copy and Paste Text',
  'small-fonts': 'Small Caps Generator — Small Capital Letters Copy and Paste',
  'zalgo-fonts': 'Zalgo Text Generator — Glitched Unicode Copy & Paste',
  'cursed-fonts': 'Cursed Font Generator - Cursed Text Copy & Paste',
  'backwards-text': 'Backwards Text Generator',
  'serif-fonts': 'Serif Font Generator - Classic Serif Text Copy and Paste',
  'underline-text': 'Underline Text Generator - Copy and Paste Underlined Text',
  'strikethrough-text': 'Strikethrough Text Copy and Paste — 8 Unicode Styles',
  'cute-fonts': 'Cute Font Generator - Cute Text Copy and Paste',
  // Head noun is "Text", not "Font" — the fallback would read "Gothic Font
  // Generator" while the title targets "Gothic Text Generator".
  'gothic-fonts': 'Gothic Text Generator - Copy & Paste',
  'bold-fonts': 'Bold Text Generator - Copy & Paste',
  'cross-out-fonts': 'Cross Out Text Generator - Copy & Paste',
};

export const getCategoryWebAppSchema = (categoryName, slug, description) => ({
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: `${categoryName} Generator`,
  url: `${SITE_URL}/fonts/${slug}/`,
  applicationCategory: 'UtilityApplication',
  operatingSystem: 'All',
  offers: {
    '@type': 'Offer',
    price: '0',
    priceCurrency: 'USD',
  },
  description,
  featureList:
    slug === 'backwards-text'
      ? [
          'Backwards text',
          'Mirrored text',
          'Backwards words only',
          'Mirrored letters only',
          'Mirrored words and letters',
          'Reverse word order',
          'Upside-down text',
          'Copy and download outputs',
        ]
      : slug === 'underline-text'
      ? [
          'Classic underline',
          'Bold underline',
          'Italic underline',
          'Script underline',
          'Double underline',
          'Wavy underline',
          'Dotted underline',
          'Boxed underline (overline + underline)',
          'Copy and paste outputs',
        ]
      : [
          'Copy and paste font styles',
          'No sign-up required',
          'Works on iOS and Android',
          'Live text preview',
        ],
});

export const getCategorySeo = (category) => {
  const categoryName = formatCategoryName(category);
  const slug = slugifyCategory(categoryName);
  const seoName = getCategorySeoName(categoryName);
  const title =
    CATEGORY_TITLES[slug] || `${seoName} Generator - Copy & Paste Fonts | FontifyText`;
  const description =
    CATEGORY_META_DESCRIPTIONS[slug] ||
    `Generate free ${seoName.toLowerCase()} fonts online and copy-paste them into Instagram bios, TikTok captions, Discord usernames, and more. No sign-up needed.`;
  const heroDescription =
    CATEGORY_HERO_DESCRIPTIONS[slug] ||
    `Create, preview, and copy stylish ${seoName.toLowerCase()} fonts for social media bios, captions, usernames, and messages.`;
  // The fallback must keep the head noun. It used to read `${seoName} Generator`,
  // which produced "Instagram Generator" on a page whose title targets
  // "Instagram Font Generator" — nobody searches the former. Every entry in
  // CATEGORY_H1S was someone adding that word back one slug at a time; this
  // makes it the default so the remaining 22 stop dropping it. Categories whose
  // head noun is "Text" rather than "Font" are listed in CATEGORY_H1S.
  const h1 = CATEGORY_H1S[slug] || `${seoName} Font Generator - Copy & Paste`;

  return {
    title,
    description,
    heroDescription,
    h1,
    canonical: `${SITE_URL}/fonts/${slug}/`,
  };
};

export const FONTS_INDEX_TITLE =
  'Font Generators - Free Copy & Paste | FontifyText';

export const FONTS_INDEX_DESCRIPTION =
  'Browse 25+ free font generators on FontifyText. Copy-paste bold, cursive, gothic, and fancy Unicode fonts for Instagram bios, captions, and usernames.';

export const ABOUT_TITLE = 'About FontifyText - Free Fancy Text Generator';

export const ABOUT_DESCRIPTION =
  'Learn about FontifyText - the free Unicode font generator tool. Our mission: make stylish copy-paste text accessible to everyone on any social media platform.';

export const CONTACT_TITLE = 'Contact FontifyText - Get Support';

export const CONTACT_DESCRIPTION =
  'Contact FontifyText for support or feedback about the font generator, bug reports, content corrections, accessibility, or new text style suggestions.';

export const PRIVACY_TITLE = 'Privacy Policy - FontifyText';

export const PRIVACY_DESCRIPTION =
  "Read FontifyText's privacy policy. Learn how we collect, use, and protect your data when using our free Unicode font generator tool. Your privacy matters.";

export const COOKIE_TITLE = 'Cookie Policy - FontifyText';

export const COOKIE_DESCRIPTION =
  'What cookies FontifyText uses for analytics and advertising, how consent is applied before any Google tag loads, and how to change your choice at any time.';

export const TERMS_TITLE = 'Terms & Conditions — FontifyText';

export const TERMS_DESCRIPTION =
  'FontifyText terms and conditions - covers acceptable use, intellectual property, and service limitations for our free Unicode copy-paste font generator tool.';

export const getRelatedCategories = (category, categories) => {
  const currentSlug = slugifyCategory(category);
  const preferredSlugs = {
    // Typography style clusters
    'bold-fonts':            ['cursive-fonts', 'italics-fonts'],
    'cursive-fonts':         ['script-fonts', 'calligraphy-fonts'],
    'script-fonts':          ['cursive-fonts', 'calligraphy-fonts'],
    'monospace-fonts':       ['small-fonts', 'bold-fonts'],
    'italics-fonts':         ['bold-fonts', 'cursive-fonts'],
    'calligraphy-fonts':     ['script-fonts', 'cursive-fonts'],
    'gothic-fonts':          ['bold-fonts', 'calligraphy-fonts'],
    'serif-fonts':           ['calligraphy-fonts', 'bold-fonts'],
    // Platform clusters
    'instagram-fonts':       ['facebook-fonts', 'whatsapp-fonts'],
    'facebook-fonts':        ['instagram-fonts', 'twitter-fonts'],
    'twitter-fonts':         ['instagram-fonts', 'discord-fonts'],
    'discord-fonts':         ['monospace-fonts', 'twitch-fonts'],
    'twitch-fonts':          ['discord-fonts', 'cool-fonts'],
    'whatsapp-fonts':        ['instagram-fonts', 'facebook-fonts'],
    // Dark/horror cluster
    'creepy-fonts':          ['gothic-fonts', 'zalgo-fonts'],
    'zalgo-fonts':           ['glitch-fonts', 'creepy-fonts'],
    'glitch-fonts':          ['zalgo-fonts', 'cursed-fonts'],
    'cursed-fonts':          ['glitch-fonts', 'creepy-fonts'],
    // Decorative cluster
    'fancy-fonts':           ['decorated-fonts', 'cool-fonts'],
    'decorated-fonts':       ['heart-decorated-fonts', 'cute-fonts'],
    'heart-decorated-fonts': ['cute-fonts', 'decorated-fonts'],
    'swirly-fonts':          ['decorated-fonts', 'aesthetic-fonts'],
    'cute-fonts':            ['heart-decorated-fonts', 'aesthetic-fonts'],
    // Compact/readable cluster
    'bubble-fonts':          ['bold-fonts', 'symbolic-fonts'],
    'small-fonts':           ['bubble-fonts', 'aesthetic-fonts'],
    'symbolic-fonts':        ['cool-fonts', 'fancy-fonts'],
    // Utility cluster
    'cross-out-fonts':       ['strikethrough-text', 'underline-text'],
    'backwards-text':        ['cross-out-fonts', 'glitch-fonts'],
    'underline-text':        ['strikethrough-text', 'bold-fonts'],
    'strikethrough-text':    ['cross-out-fonts', 'underline-text'],
    'aesthetic-fonts':       ['ethereal-fonts', 'calligraphy-fonts'],
    'cool-fonts':            ['monospace-fonts', 'fancy-fonts'],
    // Dreamy/soft cluster
    'ethereal-fonts':        ['aesthetic-fonts', 'calligraphy-fonts'],
  };

  const bySlug = new Map(categories.map((item) => [slugifyCategory(item), item]));
  const preferred = (preferredSlugs[currentSlug] || [])
    .map((slug) => bySlug.get(slug))
    .filter(Boolean);
  const fallback = categories.filter((item) => slugifyCategory(item) !== currentSlug);

  return [...new Set([...preferred, ...fallback])].slice(0, 2);
};

